import React, { useRef, useEffect } from 'react';
import { VideoTileProps } from './types';

// Custom Video Tile Component
export const VideoTile: React.FC<VideoTileProps> = ({ 
    participant, 
    isLocal, 
    isTeacher, 
    isPinned, 
    isActiveSpeaker, 
    onClick, 
    className = '',
    selectedAvatar,
    isMirrored = false
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isVideoEnabled, setIsVideoEnabled] = React.useState(true);
    
    // Default avatars
    const defaultAvatars = [
        'https://api.dicebear.com/7.x/avataaars/svg?seed=default1',
        'https://api.dicebear.com/7.x/avataaars/svg?seed=default2',
        'https://api.dicebear.com/7.x/avataaars/svg?seed=default3',
        'https://api.dicebear.com/7.x/avataaars/svg?seed=default4',
        'https://api.dicebear.com/7.x/avataaars/svg?seed=default5'
    ];
    
    const currentAvatar = selectedAvatar || defaultAvatars[0];
    
    useEffect(() => {
        let retryCount = 0;
        const maxRetries = 5;
        let retryInterval: NodeJS.Timeout | null = null;

        const attachVideoTrack = async () => {
            if (!videoRef.current || !participant.trackPublications) return;

            // Get all tracks and find the appropriate one to display
            const allTracks = [...participant.trackPublications.values()] as any[];
            
            console.log(`🎥 Track debug for ${participant.identity}:`, {
                allTracks: allTracks.map(pub => ({
                    kind: pub.kind,
                    source: pub.source,
                    isMuted: pub.isMuted,
                    trackName: pub.trackName,
                    hasTrack: !!pub.track,
                    hasVideoTrack: !!pub.videoTrack,
                    trackState: pub.track?.state,
                    streamActive: pub.track?.mediaStream?.active
                }))
            });

            // For screen share layout, prioritize screen share track
            const screenShareTrack = allTracks.find(pub => {
                // Only consider as screen share if source is explicitly 'screen_share' or 'screen'
                const isScreenShare = pub.source === 'screen_share' || pub.source === 'screen';
                const hasValidTrack = pub.track && pub.track.mediaStream?.active;
                
                console.log(`🖥️ Screen share track check for ${pub.trackName || 'unnamed'}:`, {
                    isScreenShare,
                    hasValidTrack,
                    source: pub.source,
                    kind: pub.kind,
                    trackState: pub.track?.state,
                    streamActive: pub.track?.mediaStream?.active
                });
                
                return isScreenShare && hasValidTrack;
            });

            // For regular video, use camera track
            const cameraTrack = allTracks.find(pub => {
                const isVideoTrack = pub.kind === 'video';
                const isCameraSource = pub.source === 'camera' || !pub.source;
                const hasValidTrack = pub.track && pub.track.mediaStream?.active;
                
                console.log(`📷 Camera track check for ${pub.trackName || 'unnamed'}:`, {
                    isVideoTrack,
                    isCameraSource,
                    hasValidTrack,
                    source: pub.source,
                    kind: pub.kind,
                    trackState: pub.track?.state,
                    streamActive: pub.track?.mediaStream?.active
                });
                
                return isVideoTrack && isCameraSource && hasValidTrack;
            });

            // Choose track based on className
            const isScreenShare = className?.includes('screen-share-video');
            const videoTrackPublication = isScreenShare ? screenShareTrack : cameraTrack;
            
            console.log(`🎥 Selected track for ${participant.identity}:`, {
                isScreenShare,
                selectedTrack: videoTrackPublication ? {
                    source: videoTrackPublication.source,
                    kind: videoTrackPublication.kind,
                    trackName: videoTrackPublication.trackName,
                    trackState: videoTrackPublication.track?.state,
                    streamActive: videoTrackPublication.track?.mediaStream?.active
                } : null,
                screenShareAvailable: !!screenShareTrack,
                cameraAvailable: !!cameraTrack
            });

            console.log(`🎥 Selected track for ${participant.identity}:`, {
                isScreenShare,
                hasScreenShare: !!screenShareTrack,
                hasCameraTrack: !!cameraTrack,
                selectedTrack: videoTrackPublication ? {
                    kind: videoTrackPublication.kind,
                    source: videoTrackPublication.source,
                    isMuted: videoTrackPublication.isMuted,
                    hasTrack: !!videoTrackPublication.track
                } : null
            });

            if (videoTrackPublication?.videoTrack) {
                const videoElement = videoRef.current;
                const videoTrack = (videoTrackPublication as any).videoTrack;

            // Log track debug info
            console.log(`🎥 Video track debug for ${participant.identity}:`, {
                allTracks: allTracks.map(pub => ({
                    source: pub.source,
                    kind: pub.kind,
                    isMuted: pub.isMuted,
                    hasVideoTrack: !!pub.videoTrack
                })),
                selectedTrack: videoTrackPublication ? {
                    source: (videoTrackPublication as any).source,
                    kind: (videoTrackPublication as any).kind,
                    isMuted: videoTrackPublication.isMuted,
                    hasVideoTrack: !!(videoTrackPublication as any).videoTrack
                } : null
            });

                try {
                    // Check if video element already has a source to prevent conflicts
                    if (videoElement.srcObject) {
                        console.log(`🔄 Video element already has source, detaching first for ${participant.identity}`);
                        videoTrack.detach(videoElement);
                        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
                    }

                    await videoTrack.attach(videoElement);
                    videoElement.autoplay = true;
                    videoElement.playsInline = true;
                    videoElement.muted = true;

                    // Apply mirror effect for local camera based on settings
                    if (isLocal) {
                        videoElement.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                    }

                    console.log(`✅ Video track attached successfully for ${participant.identity}`);
                    if (retryInterval) {
                        clearInterval(retryInterval);
                        retryInterval = null;
                    }
                } catch (error) {
                    console.error(`❌ Failed to attach video track for ${participant.identity}:`, error);
                    if (retryCount < maxRetries) {
                        retryCount++;
                        console.log(`🔄 Retrying video attachment (${retryCount}/${maxRetries})...`);
                        if (!retryInterval) {
                            retryInterval = setInterval(attachVideoTrack, 2000); // Increased delay
                        }
                    }
                }

                // Listen for track mute/unmute
                const handleMute = () => {
                    console.log(`🔇 Video track muted for ${participant.identity}`);
                    setIsVideoEnabled(false);
                    // Don't manipulate video element display here - let CSS handle it
                };

                const handleUnmute = () => {
                    console.log(`🔊 Video track unmuted for ${participant.identity}`);
                    setIsVideoEnabled(true);
                    // Don't manipulate video element display here - let CSS handle it
                };

                videoTrack.on('muted', handleMute);
                videoTrack.on('unmuted', handleUnmute);

                // Initial mute state
                if (videoTrack.isMuted) {
                    handleMute();
                } else {
                    setIsVideoEnabled(true);
                }

                return () => {
                    if (retryInterval) {
                        clearInterval(retryInterval);
                    }
                    if (videoElement && videoTrack) {
                        try {
                            // Properly close any VideoFrames before detaching
                            if (videoElement.srcObject) {
                                const stream = videoElement.srcObject as MediaStream;
                                stream.getVideoTracks().forEach(track => {
                                    if (track.readyState === 'live') {
                                        track.stop();
                                    }
                                });
                                // Clear the srcObject to release VideoFrames
                                videoElement.srcObject = null;
                            }
                            
                            // Pause and clear video element
                            videoElement.pause();
                            videoElement.load(); // Reset video element
                            
                            videoTrack.off('muted', handleMute);
                            videoTrack.off('unmuted', handleUnmute);
                            videoTrack.detach(videoElement);
                            console.log(`🔌 Video track detached for ${participant.identity}`);
                        } catch (error) {
                            console.warn(`⚠️ Error detaching video track for ${participant.identity}:`, error);
                        }
                    }
                };
            } else {
                console.log(`⏳ Video track not ready yet for ${participant.identity}, will retry...`);
                if (retryCount < maxRetries && !retryInterval) {
                    retryCount++;
                    retryInterval = setInterval(attachVideoTrack, 1000);
                }
            }
        };

        attachVideoTrack();

        return () => {
            if (retryInterval) {
                clearInterval(retryInterval);
            }
        };
    }, [participant.videoTrackPublications, participant.identity, isLocal, className, isMirrored, participant.trackPublications]);

    // Update mirror effect when isMirrored prop changes - prevent initial flip
    const lastMirrorStateRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (isLocal && videoRef.current) {
            // Only apply if the state actually changed from the last known state
            if (lastMirrorStateRef.current !== null && lastMirrorStateRef.current !== isMirrored) {
                videoRef.current.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                console.log(`🔄 Mirror state changed for ${participant.identity}: ${isMirrored ? 'flipped' : 'normal'}`);
            } else if (lastMirrorStateRef.current === null) {
                // First render - set initial state without animation
                videoRef.current.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                lastMirrorStateRef.current = isMirrored;
                console.log(`🎬 Initial mirror state for ${participant.identity}: ${isMirrored ? 'flipped' : 'normal'}`);
            }
            lastMirrorStateRef.current = isMirrored;
        }
    }, [isMirrored, isLocal, participant.identity]);

    return (
        <div 
            className={`custom-video-tile bg-gray-800 cursor-pointer ${
                isPinned ? 'pinned' : ''
            } ${isActiveSpeaker ? 'active-speaker' : ''} ${className}`}
            onClick={onClick}
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative',
                border: 'none', // Removed purple/colored borders to prevent recording outline
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease'
            }}
        >
            {/* Video Element */}
            <video
                ref={videoRef}
                className="w-full h-full object-cover"
                data-local={isLocal ? "true" : "false"}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#000',
                    background: '#000',
                    display: isVideoEnabled ? 'block' : 'none',
                    transition: 'opacity 0.2s ease-in-out'
                }}
                onLoadStart={() => console.log(`🎬 Video load started for ${participant.identity}`)}
                onLoadedData={() => console.log(`✅ Video data loaded for ${participant.identity}`)}
                onError={(e) => console.error(`❌ Video error for ${participant.identity}:`, e)}
                onStalled={() => console.warn(`⚠️ Video stalled for ${participant.identity}`)}
                onSuspend={() => console.warn(`⚠️ Video suspended for ${participant.identity}`)}
                onWaiting={() => console.warn(`⏳ Video waiting for data for ${participant.identity}`)}
                onCanPlay={() => console.log(`▶️ Video can play for ${participant.identity}`)}
                onPlay={() => console.log(`▶️ Video playing for ${participant.identity}`)}
            />
            
            {/* Avatar Display when video is off */}
            {!isVideoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                    <div className="relative">
                        <img
                            src={currentAvatar}
                            alt={`${participant.name || participant.identity} avatar`}
                            className="w-24 h-24 rounded-full object-cover border-4 border-white/20"
                            onError={(e) => {
                                // Fallback to initials if avatar fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                    parent.innerHTML = `
                                        <div class="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold border-4 border-white/20">
                                            ${(participant.name || participant.identity).charAt(0).toUpperCase()}
                                        </div>
                                    `;
                                }
                            }}
                        />
                        {/* Video Off Indicator */}
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 16l4-4-4-4"></path>
                                <path d="M20 12H4"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Participant Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-medium truncate">
                            {participant.name || participant.identity}
                        </span>
                        {isTeacher && (
                            <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                Teacher
                            </span>
                        )}
                        {isLocal && (
                            <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                You
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {/* Video Status */}
                        <div className={`w-2 h-2 rounded-full ${
                            participant.videoTrackPublications.size > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        {/* Audio Status */}
                        <div className={`w-2 h-2 rounded-full ${
                            participant.audioTrackPublications.size > 0 && !participant.isMicrophoneEnabled ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                    </div>
                </div>
            </div>
            
            {/* Speaking Indicator */}
            {isActiveSpeaker && (
                <div className="absolute top-2 right-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
            )}
            
            {/* Pinned Indicator */}
            {isPinned && (
                <div className="absolute top-2 left-2">
                    <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3 3-3 3 3 3-3 3-3-3 3-3-3-3z"></path>
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};
